/**
 * Statistics printed on the marketing pages (05, 07, 08, 09, 10).
 *
 * Every value is a preformatted display string, because the template mixes units and
 * suffixes that are part of the design ("$6.8B+", "22,700+", "45.5M"). Formatting is
 * therefore the caller's job, not the page's.
 *
 * These are placeholders until the services behind them exist - each renders as a dash.
 * To wire a metric up, fill the corresponding key; nothing else in the pages changes.
 */

export const DASH = '-';

export const EMPTY_BOV_STATS = {
    // ---- page 05, Capital Flows
    // Neither of these is read today - Capital Flows prints the origin split from its own
    // template content, and owns the year labels too: the current one is derived as
    // "last complete year" rather than fixed. If a service ever supplies a real split and
    // these start being used, take the year from there rather than from the 2025 below, which
    // is only a placeholder and will be stale.
    buyerOriginHistoric: {year: '2007', tx: DASH, ca: DASH, other: DASH},
    buyerOriginCurrent: {year: '2025', tx: DASH, ca: DASH, other: DASH},
    texasBuyerGrowth: DASH,                 // e.g. "2.3x"
    multiTenantValueSold: DASH,             // e.g. "$3.3B+"
    multiTenantPropertiesSold: DASH,        // e.g. "578"
    singleTenantValueSold: DASH,
    singleTenantPropertiesSold: DASH,
    multiFamilyValueSold: DASH,
    multiFamilyPropertiesSold: DASH,
    totalValueSold: DASH,                   // e.g. "$6.8B+"
    totalPropertiesSold: DASH,              // e.g. "1,750"

    // ---- page 07, Proprietary Database
    investors: DASH,                        // e.g. "109,562"
    properties: DASH,                       // e.g. "129,210"
    agents: DASH,                           // e.g. "22,700+"
    investmentFocus: DASH,                  // e.g. "100%"

    // ---- page 08, The Texas Advantage
    closings: DASH,                         // e.g. "1,750+"
    aggregateVolume: DASH,                  // e.g. "$6.8B+"

    // ---- page 09, Asset Class Coverage
    totalClosings: DASH,                    // e.g. "1,740"
    totalVolume: DASH,                      // e.g. "$6.38B"
    totalSqftSold: DASH,                    // e.g. "45.5M"
    assetClasses: [
        {name: 'Retail', closings: DASH, volume: DASH},
        {name: 'Office', closings: DASH, volume: DASH},
        {name: 'Industrial', closings: DASH, volume: DASH},
        {name: 'Multi-Family', closings: DASH, volume: DASH},
    ],

    // ---- page 10, The Team
    // [{section: 'LEADERSHIP', members: [{name, title, photo}]}]
    teamSections: [],
};

/**
 * Shallow-merges whatever the caller has managed to load over the dash defaults, so a
 * partially wired page still prints correctly.
 */
export function withBovStats(stats = {}) {
    return {
        ...EMPTY_BOV_STATS,
        ...stats,
        buyerOriginHistoric: {...EMPTY_BOV_STATS.buyerOriginHistoric, ...(stats.buyerOriginHistoric || {})},
        buyerOriginCurrent: {...EMPTY_BOV_STATS.buyerOriginCurrent, ...(stats.buyerOriginCurrent || {})},
        assetClasses: stats.assetClasses && stats.assetClasses.length
            ? stats.assetClasses
            : EMPTY_BOV_STATS.assetClasses,
        teamSections: stats.teamSections || EMPTY_BOV_STATS.teamSections,
    };
}

/**
 * Percentages drive the pie wedges on page 05. While a share is a dash there is nothing
 * to draw, so the chart falls back to three equal segments rendered in a flat tint.
 */
export function toShare(value) {
    const parsed = parseFloat(String(value).replace('%', ''));
    return isNaN(parsed) ? null : parsed;
}
